import React, { useEffect, createContext, useState, ReactNode } from 'react';
import { ToastVariant } from './Toast.utils';

interface ToastProps {
  message: string;
  variant?: ToastVariant;
  duration?: number;
  onClose: () => void;
}

// Función para crear y mostrar un Toast
// No export of non-component utilities from this file to satisfy react-refresh

// Componente React (por si se necesita usar como componente)
export const Toast: React.FC<ToastProps> = ({
  message,
  variant = 'default',
  duration = 5000,
  onClose,
}) => {
  useEffect(() => {
    if (duration > 0) {
      const timer = setTimeout(onClose, duration);
      return () => clearTimeout(timer);
    }
  }, [duration, onClose]);

  // Determinar las clases según la variante
  let variantClasses = '';
  let Icon = null;

  switch (variant) {
    case 'success':
      variantClasses = 'bg-success/10 border-success/30 text-success';
      Icon = () => (
        <svg
          xmlns='http://www.w3.org/2000/svg'
          width='16'
          height='16'
          viewBox='0 0 24 24'
          fill='none'
          stroke='currentColor'
          strokeWidth='2'
          strokeLinecap='round'
          strokeLinejoin='round'
        >
          <path d='M22 11.08V12a10 10 0 1 1-5.93-9.14'></path>
          <polyline points='22 4 12 14.01 9 11.01'></polyline>
        </svg>
      );
      break;
    case 'error':
      variantClasses = 'bg-destructive/10 border-destructive/30 text-destructive';
      Icon = () => (
        <svg
          xmlns='http://www.w3.org/2000/svg'
          width='16'
          height='16'
          viewBox='0 0 24 24'
          fill='none'
          stroke='currentColor'
          strokeWidth='2'
          strokeLinecap='round'
          strokeLinejoin='round'
        >
          <circle cx='12' cy='12' r='10'></circle>
          <line x1='12' y1='8' x2='12' y2='12'></line>
          <line x1='12' y1='16' x2='12' y2='16'></line>
        </svg>
      );
      break;
    case 'warning':
      variantClasses = 'bg-warning/10 border-warning/30 text-warning';
      Icon = () => (
        <svg
          xmlns='http://www.w3.org/2000/svg'
          width='16'
          height='16'
          viewBox='0 0 24 24'
          fill='none'
          stroke='currentColor'
          strokeWidth='2'
          strokeLinecap='round'
          strokeLinejoin='round'
        >
          <path d='M10.29 3.86L1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z'></path>
          <line x1='12' y1='9' x2='12' y2='13'></line>
          <line x1='12' y1='17' x2='12' y2='17'></line>
        </svg>
      );
      break;
    default:
      variantClasses = 'bg-card text-card-foreground border-border';
      Icon = () => (
        <svg
          xmlns='http://www.w3.org/2000/svg'
          width='16'
          height='16'
          viewBox='0 0 24 24'
          fill='none'
          stroke='currentColor'
          strokeWidth='2'
          strokeLinecap='round'
          strokeLinejoin='round'
        >
          <circle cx='12' cy='12' r='10'></circle>
          <line x1='12' y1='16' x2='12' y2='12'></line>
          <line x1='12' y1='8' x2='12' y2='8'></line>
        </svg>
      );
  }

  return (
    <div className={`p-4 rounded-md border shadow-md ${variantClasses} max-w-sm`}>
      <div className='flex items-center justify-between'>
        <div className='flex items-center gap-2'>
          <div className='flex-shrink-0'>{Icon && <Icon />}</div>
          <p className='text-sm font-medium'>{message}</p>
        </div>
        <button
          onClick={onClose}
          className='ml-4 text-current opacity-70 hover:opacity-100'
          aria-label='Cerrar'
        >
          <svg
            xmlns='http://www.w3.org/2000/svg'
            width='16'
            height='16'
            viewBox='0 0 24 24'
            fill='none'
            stroke='currentColor'
            strokeWidth='2'
            strokeLinecap='round'
            strokeLinejoin='round'
          >
            <line x1='18' y1='6' x2='6' y2='18'></line>
            <line x1='6' y1='6' x2='18' y2='18'></line>
          </svg>
        </button>
      </div>
    </div>
  );
};

export default Toast;

// Toast Context
interface ToastContextType {
  toasts: Array<{
    id: string;
    message: string;
    variant: ToastVariant;
    duration: number;
  }>;
  addToast: (message: string, variant?: ToastVariant, duration?: number) => void;
  removeToast: (id: string) => void;
}

const ToastContext = createContext<ToastContextType | undefined>(undefined);

export const ToastProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const [toasts, setToasts] = useState<Array<{
    id: string;
    message: string;
    variant: ToastVariant;
    duration: number;
  }>>([]);

  const addToast = (message: string, variant: ToastVariant = 'default', duration: number = 5000) => {
    const id = Math.random().toString(36).substr(2, 9);
    setToasts(prev => [...prev, { id, message, variant, duration }]);
  };

  const removeToast = (id: string) => {
    setToasts(prev => prev.filter(toast => toast.id !== id));
  };

  return (
    <ToastContext.Provider value={{ toasts, addToast, removeToast }}>
      {children}
      <div className="fixed top-4 right-4 z-50 space-y-2">
        {toasts.map(toast => (
          <Toast
            key={toast.id}
            message={toast.message}
            variant={toast.variant}
            duration={toast.duration}
            onClose={() => removeToast(toast.id)}
          />
        ))}
      </div>
    </ToastContext.Provider>
  );
};
